import { Form } from 'antd';
import { useCallback, useContext, useRef, useState, useMemo } from 'react';

import {
  RIGHTS_RECORDS,
  RIGHTS_IS_VIEW_REMINDERS,
} from '@app/constants/accessRights';
import { FRONT_WARNING, SUCCESS } from '@app/constants/alerts';
import {
  useAccessRights,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import { ReminderTypes } from '@app/pages/Notifications/constants/reminderTypes';
import { useRecordNotificationsEffects } from '@app/pages/Notifications/hooks/useRecordNotificationsEffects';
import { NotificationContext } from '@app/providers/NotificationProvider';
import { handleCatchError } from '@app/services';
import {
  useGetRecordRemindersQuery,
  useSetRecordRemindersMutation,
} from '@app/services/rtkQuery/notifications';
import type { NRecordReminders } from '@app/types';

import type { RefObject } from 'react';

const { Second, Create, Before, After } = ReminderTypes;
const MINUTES_IN_HOUR = 60;

export const useRecordNotifications = () => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const [defaultIntegrations, setDefaultIntegrations] = useState<
    NRecordReminders.ModelBranchRecordReminderIntegration[]
  >([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [sendByLastActiveIntegration, setSendByLastActiveIntegration] =
    useState(false);

  const [form] = Form.useForm();

  const createRecordBoxRef = useRef<HTMLDivElement>(null);
  const visitReminderBoxRef = useRef<HTMLDivElement>(null);
  const visitDayReminderBoxRef = useRef<HTMLDivElement>(null);

  const [notifications, setNotifications] = useState<
    (NRecordReminders.ModelRecordReminder & { frontId: string })[]
  >([]);

  const { hasUserRight: hasUserPermission } = useAccessRights(
    RIGHTS_RECORDS,
    RIGHTS_IS_VIEW_REMINDERS
  );

  const {
    userAccessRights: {
      Records: { IsEditReminders },
    },
  } = useNewUserAccessRights();

  const isSaveDisabled = false;

  const { data: pageServerData } = useGetRecordRemindersQuery(
    {
      params: {
        ActiveBranchId: activeBranchId,
      },
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [startNotificationsSaving] = useSetRecordRemindersMutation();

  const addInitialBoxData = useCallback(
    (
      notificationType: NRecordReminders.ReminderType,
      notificationKey: keyof NRecordReminders.ModelRecordReminder,
      formItemName: string
    ) => {
      if (pageServerData) {
        const initialBoxData = pageServerData.RecordReminders?.find(
          ({ ReminderType }) => ReminderType === notificationType
        )?.[notificationKey];

        if (initialBoxData) {
          form.setFieldsValue({ [formItemName]: initialBoxData });
        }
      }
    },
    [pageServerData, form]
  );

  const addInterval = useCallback(
    (
      notificationType: NRecordReminders.ReminderType,
      formItemNameMinutes: string,
      formItemNameHours: string
    ) => {
      if (pageServerData) {
        const initialIntervalInMin = pageServerData.RecordReminders?.find(
          ({ ReminderType }) => ReminderType === notificationType
        )?.ReminderMinutes;
        const areFewIntervals = notificationType === Second;

        const hours = initialIntervalInMin
          ? Math.floor(+initialIntervalInMin / MINUTES_IN_HOUR)
          : 0;
        const minutes = initialIntervalInMin
          ? +initialIntervalInMin % MINUTES_IN_HOUR
          : 0;

        if (areFewIntervals) {
          const boundIntervals = notifications.filter(
            ({ ReminderType }) => ReminderType === Second
          );

          boundIntervals.map(({ ReminderMinutes, frontId }) => {
            const localHours = ReminderMinutes
              ? Math.floor(+ReminderMinutes / MINUTES_IN_HOUR)
              : 0;
            const localMinutes = ReminderMinutes
              ? +ReminderMinutes % MINUTES_IN_HOUR
              : 0;

            form.setFieldsValue({
              [formItemNameMinutes + frontId]: localMinutes,
              [formItemNameHours + frontId]: localHours,
            });
          });
        }

        form.setFieldsValue({
          [formItemNameMinutes]: minutes,
          [formItemNameHours]: hours,
        });
      }
    },
    [pageServerData, form, notifications]
  );

  const addInitialIntervals = useCallback(() => {
    if (pageServerData) {
      addInterval(
        Create,
        'createRecordIntervalMinutes',
        'createRecordIntervalHours'
      );

      addInterval(
        Second,
        'generalReminderIntervalMinutes',
        'generalReminderIntervalHours'
      );

      addInterval(Before, 'visitDayIntervalMinutes', 'visitDayIntervalHours');

      addInterval(After, 'feedbackIntervalMinutes', 'feedbackIntervalHours');
    }
  }, [pageServerData, addInterval]);

  const validateMessage = useCallback(
    (
      boxStatusId: string,
      messageItemName: string,
      boxRef: RefObject<HTMLDivElement> | null,
      withoutScroll: boolean = false
    ) => {
      const isBoxEnabled = form.getFieldValue(boxStatusId);
      let hasError = false;

      if (isBoxEnabled) {
        const isEmptyMessage =
          form.getFieldValue(messageItemName).trim() === '';

        if (isEmptyMessage) {
          if (!withoutScroll && boxRef?.current) {
            boxRef.current.scrollIntoView({ behavior: 'smooth' });
          }

          form.setFields([
            {
              name: messageItemName,
              errors: ['Поле не может быть пустым'],
            },
          ]);

          hasError = true;
        }
      }

      return hasError;
    },
    [form]
  );

  useRecordNotificationsEffects({
    pageServerData,
    form,
    setDefaultIntegrations,
    addInitialIntervals,
    addInitialBoxData,
    setNotifications,
    setSendToAll,
    setSendByLastActiveIntegration,
  });

  const validateMultipleIntervals = useCallback(() => {
    const secondReminders = notifications.filter(
      ({ ReminderType }) => ReminderType === Second
    );

    const minutesValues = secondReminders.map(({ frontId }) => {
      const hours =
        form.getFieldValue(`generalReminderIntervalHours${frontId}`) || 0;
      const minutes =
        form.getFieldValue(`generalReminderIntervalMinutes${frontId}`) || 0;

      return hours * MINUTES_IN_HOUR + minutes;
    });

    const hasDuplicates = minutesValues.some(
      (value, index) => minutesValues.indexOf(value) !== index
    );

    if (hasDuplicates) {
      form.setFields([
        {
          name: 'generalReminderIntervalMinutes',
          errors: ['Интервалы не могут совпадать'],
        },
      ]);

      return true;
    }

    secondReminders.forEach(({ frontId }) => {
      validateMessage(
        `generalReminderStatus${frontId}`,
        `generalReminderMessage${frontId}`,
        null
      );
    });
  }, [notifications, validateMessage, form]);

  const validateSendingData = useCallback(() => {
    const hasCreateRecordError = validateMessage(
      'createRecordStatus',
      'createRecordMessage',
      createRecordBoxRef
    );
    const hasGeneralReminderMessageError = validateMessage(
      'generalReminderStatus',
      'generalReminderMessage',
      visitReminderBoxRef
    );
    const hasVisitDayReminderError = validateMessage(
      'visitDayReminderStatus',
      'visitDayReminderMessage',
      visitDayReminderBoxRef
    );
    const hasFeedbackError = validateMessage(
      'feedbackStatus',
      'feedbackMessage',
      null,
      true
    );

    const hasDuplicateIntervals = validateMultipleIntervals();

    if (
      hasCreateRecordError ||
      hasGeneralReminderMessageError ||
      hasDuplicateIntervals ||
      hasVisitDayReminderError ||
      hasFeedbackError
    ) {
      if (hasDuplicateIntervals) {
        visitReminderBoxRef.current?.scrollIntoView({ behavior: 'smooth' });

        form.setFields([
          {
            name: 'generalReminderMessage',
            errors: ['Интервалы не могут совпадать'],
          },
        ]);
      }

      return true;
    }

    return false;
  }, [
    form,
    validateMessage,
    validateMultipleIntervals,
    createRecordBoxRef,
    visitReminderBoxRef,
    visitDayReminderBoxRef,
  ]);

  const handleSave = useCallback(async () => {
    const hasError = validateSendingData();

    if (hasError) {
      return;
    }

    try {
      const getActualReminderMinutes = (
        reminderType: NRecordReminders.ReminderType | null,
        reminderMinutes: number,
        frontId: string
      ) => {
        const transformToMinutes = (hoursKey: string, minutesKey: string) =>
          form.getFieldValue(hoursKey) * MINUTES_IN_HOUR +
          form.getFieldValue(minutesKey);

        switch (reminderType) {
          case Create:
            return transformToMinutes(
              'createRecordIntervalHours',
              'createRecordIntervalMinutes'
            );
          case Before:
            return transformToMinutes(
              'visitDayIntervalHours',
              'visitDayIntervalMinutes'
            );
          case After:
            return transformToMinutes(
              'feedbackIntervalHours',
              'feedbackIntervalMinutes'
            );
          case Second:
            return transformToMinutes(
              `generalReminderIntervalHours${frontId}`,
              `generalReminderIntervalMinutes${frontId}`
            );
          default:
            return reminderMinutes;
        }
      };

      const getActualReminderText = (
        reminderType: NRecordReminders.ReminderType | null
      ) => {
        switch (reminderType) {
          case Create:
            return form.getFieldValue('createRecordMessage');
          case Second:
            return form.getFieldValue('generalReminderMessage');
          case Before:
            return form.getFieldValue('visitDayReminderMessage');
          case After:
            return form.getFieldValue('feedbackMessage');
          default:
            showNotification(
              'Произошла ошибка при определении типа уведомления',
              FRONT_WARNING
            );
            return '';
        }
      };

      const getActualReminderStatus = (
        reminderType: NRecordReminders.ReminderType | null
      ) => {
        switch (reminderType) {
          case Create:
            return form.getFieldValue('createRecordStatus') ?? false;
          case Second:
            return form.getFieldValue('generalReminderStatus') ?? false;
          case Before:
            return form.getFieldValue('visitDayReminderStatus') ?? false;
          case After:
            return form.getFieldValue('feedbackStatus') ?? false;
          default:
            showNotification(
              'Произошла ошибка при определении статуса включения уведомления',
              FRONT_WARNING
            );
            return false;
        }
      };

      const getConfirmByWordsText = () => {
        if (form.getFieldValue('confirmByWords')) {
          const confirmByWordsText = form.getFieldValue('confirmByWordsText');
          const handledWords = confirmByWordsText
            .split(',')
            .map((item: string) => item.trim());
          const dataObject = {
            IsOn: true,
            Words: handledWords,
          };
          return JSON.stringify(dataObject);
        }
        return null;
      };

      const handledRecordReminders = notifications.map(
        ({ frontId, Id, ReminderType, ReminderMinutes }) => {
          const isOn = getActualReminderStatus(ReminderType);
          const text = getActualReminderText(ReminderType);
          const reminderMinutes =
            getActualReminderMinutes(ReminderType, ReminderMinutes, frontId) ||
            0;

          return {
            Id,
            ReminderType,
            IsOn: isOn,
            Text: text,
            ReminderMinutes: reminderMinutes,
            Json:
              ReminderType === ReminderTypes.Second
                ? getConfirmByWordsText()
                : null,
          };
        }
      );

      await startNotificationsSaving({
        params: {
          ActiveBranchId: activeBranchId,
        },
        data: {
          AllowedIntegrations: null,
          DefaultIntegrations: defaultIntegrations,
          RecordReminders: handledRecordReminders,
          SendByAllIntegrations: sendToAll,
          SendByLastActiveIntegration: sendByLastActiveIntegration,
        },
      }).unwrap();

      showNotification('Уведомления успешно сохранены', SUCCESS);

      form.setFields([
        {
          name: 'generalReminderMessage',
          errors: [],
        },
      ]);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    validateSendingData,
    notifications,
    startNotificationsSaving,
    activeBranchId,
    defaultIntegrations,
    sendToAll,
    sendByLastActiveIntegration,
    showNotification,
    form,
  ]);

  const deleteNotification = useCallback(
    (frontId: string) => () => {
      setNotifications((prev) =>
        prev.filter(({ frontId: localFrontId }) => localFrontId !== frontId)
      );
    },
    [setNotifications]
  );

  const createRecordReminder = useMemo(
    () =>
      notifications.find(({ ReminderType }) => ReminderType === Create) ?? null,
    [notifications]
  );

  const generalReminder = useMemo(
    () =>
      notifications.find(({ ReminderType }) => ReminderType === Second) ?? null,
    [notifications]
  );

  const visitDayReminder = useMemo(
    () =>
      notifications.find(({ ReminderType }) => ReminderType === Before) ?? null,
    [notifications]
  );

  const feedbackReminder = useMemo(
    () =>
      notifications.find(({ ReminderType }) => ReminderType === After) ?? null,
    [notifications]
  );

  return {
    defaultIntegrations,
    form,
    isSaveDisabled,
    createRecordBoxRef,
    visitReminderBoxRef,
    visitDayReminderBoxRef,
    handleSave,
    notifications,
    setNotifications,
    deleteNotification,
    hasUserPermission,
    createRecordReminder,
    generalReminder,
    visitDayReminder,
    feedbackReminder,
    setDefaultIntegrations,
    pageServerData,
    sendToAll,
    setSendToAll,
    sendByLastActiveIntegration,
    setSendByLastActiveIntegration,
    IsEditReminders,
  };
};
